import React, { useEffect, useRef, useState } from 'react'

export type CompanionPosition = { x: number; bottom: number }

const DRAG_THRESHOLD_PX = 4
const VIEWPORT_PAD_PX = 12
const NUDGE_PX = 8

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const clampBoxPosition = (
  left: number,
  top: number,
  width: number,
  height: number
): CompanionPosition => {
  const x = clamp(left, VIEWPORT_PAD_PX, window.innerWidth - width - VIEWPORT_PAD_PX)
  const clampedTop = clamp(top, VIEWPORT_PAD_PX, window.innerHeight - height - VIEWPORT_PAD_PX)
  return { x, bottom: window.innerHeight - (clampedTop + height) }
}

export const useCompanionDrag = ({
  position,
  setPosition,
  setHasDragged
}: {
  position: CompanionPosition | null
  setPosition: (value: CompanionPosition | null) => void
  setHasDragged: (value: boolean | ((prev: boolean) => boolean)) => void
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const suppressClickRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  const beginDrag = (event: React.PointerEvent) => {
    const el = wrapRef.current
    if (!el || event.button === 2) return

    const box = el.getBoundingClientRect()
    const offsetX = event.clientX - box.left
    const offsetY = event.clientY - box.top
    let moved = false

    const onMove = (ev: PointerEvent) => {
      if (
        !moved &&
        Math.abs(ev.clientX - event.clientX) + Math.abs(ev.clientY - event.clientY) <
          DRAG_THRESHOLD_PX
      ) {
        return
      }
      if (!moved) {
        moved = true
        setHasDragged(true)
        setIsDragging(true)
      }
      setPosition(
        clampBoxPosition(ev.clientX - offsetX, ev.clientY - offsetY, box.width, box.height)
      )
    }

    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      suppressClickRef.current = moved
      setIsDragging(false)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    event.preventDefault()
  }

  const consumeClickIfDragged = () => {
    if (!suppressClickRef.current) return false
    suppressClickRef.current = false
    return true
  }

  const snapBack = () => setPosition(null)

  const nudge = (dx: number, dy: number) => {
    const el = wrapRef.current
    if (!el) return
    const box = el.getBoundingClientRect()
    setPosition(clampBoxPosition(box.left + dx, box.top + dy, box.width, box.height))
    setHasDragged(true)
  }

  useEffect(() => {
    if (!position) return

    const reclamp = () => {
      const el = wrapRef.current
      if (!el) return

      const box = el.getBoundingClientRect()
      const next = clampBoxPosition(box.left, box.top, box.width, box.height)
      if (next.x !== position.x || next.bottom !== position.bottom) {
        setPosition(next)
      }
    }

    reclamp()
    window.addEventListener('resize', reclamp)
    const observer = new ResizeObserver(reclamp)
    if (wrapRef.current) observer.observe(wrapRef.current)

    return () => {
      window.removeEventListener('resize', reclamp)
      observer.disconnect()
    }
  }, [position, setPosition])

  const onKeyDown = (event: React.KeyboardEvent, onEscape?: () => void) => {
    if (event.key === 'Escape') {
      onEscape?.()
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      nudge(-NUDGE_PX, 0)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      nudge(NUDGE_PX, 0)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      nudge(0, -NUDGE_PX)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      nudge(0, NUDGE_PX)
    }
  }

  const dockStyle: React.CSSProperties = position
    ? { left: position.x, bottom: position.bottom, right: 'auto', top: 'auto' }
    : { left: 'auto', top: 'auto' }

  return {
    wrapRef,
    beginDrag,
    consumeClickIfDragged,
    snapBack,
    onKeyDown,
    isDragging,
    hasMoved: position !== null,
    dockStyle
  }
}
